import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import './MobileActionModal.scss';

export type MobileActionOption = {
  name?: string;
  active?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
  // icon?: React.ReactNode; // Future enhancement
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  options: MobileActionOption[];
  title?: string; // Optional title for the modal
  triggerRef: React.RefObject<HTMLElement>;
  align?: 'left' | 'right'; // Add align prop
};

const MobileActionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  options,
  title,
  triggerRef,
  align = 'right', // Default align to 'right'
}) => {
  // State to hold top and either left or right
  const [modalPosition, setModalPosition] = useState<{ top: number; left?: number; right?: number }>({ top: 0 });
  const [finalAlign, setFinalAlign] = useState<'left' | 'right'>(align);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && triggerRef.current && modalRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const modalRect = modalRef.current.getBoundingClientRect(); // Get modal dimensions
      const viewportWidth = window.innerWidth;

      // Position below the trigger, aligned to its right edge
      // Adjustments might be needed based on exact styling/layout
      const top = triggerRect.bottom + 8; // 8px gap

      const positionUpdate: { top: number; left?: number; right?: number } = { top };
      let currentAlign = align; // Start with the preferred alignment

      // Calculate initial position based on preferred alignment
      if (currentAlign === 'left') {
        const initialLeft = triggerRect.left;
        // Check for overflow on the right
        if (initialLeft + modalRect.width > viewportWidth) {
          currentAlign = 'right'; // Flip alignment
        }
      } else {
        // Preferred alignment is 'right'
        const initialRight = viewportWidth - triggerRect.right;
        const calculatedLeftEdge = triggerRect.right - modalRect.width;
        // Check for overflow on the left
        if (calculatedLeftEdge < 0) {
          currentAlign = 'left'; // Flip alignment
        }
      }

      // Set final position based on potentially flipped alignment
      if (currentAlign === 'left') {
        positionUpdate.left = triggerRect.left;
        positionUpdate.right = undefined;
      } else {
        // Final alignment is 'right'
        positionUpdate.right = viewportWidth - triggerRect.right;
        positionUpdate.left = undefined;
      }

      setModalPosition(positionUpdate);
      setFinalAlign(currentAlign); // Store the alignment actually used
    }
  }, [isOpen, triggerRef, align]);

  if (!isOpen) {
    return null;
  }

  const handleOptionClick = (optionOnClick?: () => void) => {
    if (optionOnClick) {
      optionOnClick(); // Execute the option's specific action
    }
    onClose(); // Close the modal after action
  };

  return (
    <div className="MobileActionModal-overlay" onClick={onClose}>
      {/* Apply calculated position */}
      <div
        ref={modalRef}
        className="MobileActionModal-content"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', // Keep position absolute
          top: `${modalPosition.top}px`,
          // Conditionally apply left or right based on state
          ...(modalPosition.left !== undefined && { left: `${modalPosition.left}px` }),
          ...(modalPosition.right !== undefined && { right: `${modalPosition.right}px` }),
        }}
      >
        {title && <div className="MobileActionModal-title">{title}</div>}
        <ul className="MobileActionModal-list">
          {options.map((option, index) => (
            <li
              key={index}
              className={classNames('MobileActionModal-option', { active: option.active, left: finalAlign === 'left' })}
              onClick={() => handleOptionClick(option.onClick)}
            >
              {/* Add icon placeholder here if needed later */}
              {option.children || option.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default MobileActionModal;
